import { Popover, PopoverContent, PopoverTrigger } from "../../shadcn/popover";
import { Button } from "../../shadcn/button";
import { CalendarIcon } from "lucide-react";
import { format, subDays } from "date-fns";
import { cn } from "../../../utils/utilsShared";
import { MonthPicker } from "../../shadcn/monthpicker";

type PresetOption = {
  label: string;
  value: number; // days to subtract from current date
  key: string;
};

type DateRange = {
  from: Date;
  to: Date;
};

const presetOptions: PresetOption[] = [
  { label: "Last 30 days", value: 30, key: "30d" },
  { label: "Last 60 days", value: 60, key: "60d" },
  { label: "Last 90 days", value: 90, key: "90d" },
];


interface MonthPickerWithPresetsProps {
  selectedMonth?: Date;
  selectedPreset?: string;
  onChange: (month: Date | undefined) => void;
  onPresetChange?: (preset: string, dateRange: DateRange) => void;
}

export const MonthPickerWithPresets: React.FC<MonthPickerWithPresetsProps> = ({
  selectedMonth,
  selectedPreset,
  onChange,
  onPresetChange,
}) => {
  const handlePresetClick = (preset: PresetOption) => {
    const today = new Date();
    const fromDate = subDays(today, preset.value);
    const dateRange: DateRange = {
      from: fromDate,
      to: today,
    };
    
    // Clear month selection when preset is selected
    onChange(undefined);
    onPresetChange?.(preset.key, dateRange);
  };

  const handleMonthSelect = (month: Date) => {
    // Clear preset selection when month is selected
    onPresetChange?.("", { from: new Date(), to: new Date() });
    onChange(month);
  };

  const getDisplayText = () => {
    if (selectedPreset) {
      const preset = presetOptions.find(p => p.key === selectedPreset);
      return preset?.label || "Select period";
    }
    if (selectedMonth) {
      return format(selectedMonth, "MMM yyyy");
    }
    return "Select period";
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[180px] min-w-[180px] custom-input justify-start text-left font-normal bg-white",
            !selectedMonth && !selectedPreset && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <span>{getDisplayText()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white">
        <div className="flex">
          {/* Month Picker */}
          <MonthPicker onMonthSelect={handleMonthSelect} selectedMonth={selectedMonth} />
          
          {/* Preset Buttons */}
          <div className="flex flex-col gap-2 p-3 border-l border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-1">Quick Select</div>
            {presetOptions.map((preset) => (
              <Button
                key={preset.key}
                variant={selectedPreset === preset.key ? "default" : "outline"}
                size="sm"
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  "text-xs font-medium transition-colors w-full justify-start",
                  selectedPreset === preset.key 
                    ? "bg-blue-600 hover:bg-blue-700 text-white" 
                    : "bg-white hover:bg-gray-50 text-gray-700 border-gray-300"
                )}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
